// AES-256-CBC encryption using the native Web Crypto API.
// Key: first 32 UTF-8 bytes of passphrase, zero-padded — matches PHP and CryptoJS.

const ENC_KEY_STORAGE = 'gp_enc_key';

export const getEncKey = () => localStorage.getItem(ENC_KEY_STORAGE) || '';
export const setEncKey = (k) => {
  if (k) localStorage.setItem(ENC_KEY_STORAGE, k);
  else localStorage.removeItem(ENC_KEY_STORAGE);
};
export const hasEncKey = () => Boolean(localStorage.getItem(ENC_KEY_STORAGE));

export function isEncrypted(obj) {
  return obj != null && obj._enc === 1 && typeof obj.iv === 'string' && typeof obj.d === 'string';
}

async function deriveCryptoKey(passphrase, usages) {
  const enc = new TextEncoder();
  // Raw 32-byte key: first 32 UTF-8 bytes of passphrase, zero-padded.
  // Matches PHP: str_pad(substr(ENCRYPTION_KEY,0,32),32,"\0")
  const src = enc.encode(passphrase).slice(0, 32);
  const raw = new Uint8Array(32);
  raw.set(src);
  return crypto.subtle.importKey('raw', raw.buffer, { name: 'AES-CBC' }, false, usages);
}

const toB64 = (buf) => btoa(String.fromCharCode(...new Uint8Array(buf)));
const fromB64 = (s) => Uint8Array.from(atob(s), (c) => c.charCodeAt(0));

export async function aesDecrypt(encObj, passphrase) {
  try {
    const key = await deriveCryptoKey(passphrase, ['decrypt']);
    const iv   = fromB64(encObj.iv);
    const data = fromB64(encObj.d);
    const plain = await crypto.subtle.decrypt({ name: 'AES-CBC', iv }, key, data);
    return { ok: true, text: new TextDecoder().decode(plain) };
  } catch (e) {
    return { ok: false, error: 'Wrong key or corrupted data' };
  }
}

export async function aesEncrypt(plaintext, passphrase) {
  const key = await deriveCryptoKey(passphrase, ['encrypt']);
  const iv = crypto.getRandomValues(new Uint8Array(16));
  const cipher = await crypto.subtle.encrypt(
    { name: 'AES-CBC', iv },
    key,
    new TextEncoder().encode(plaintext)
  );
  return { _enc: 1, iv: toB64(iv.buffer), d: toB64(cipher) };
}
